/**
 * Stepper vertical partagé (étapes 1-2-3 des parcours de création).
 * Référence visuelle : maquette Figma de Justine (node 2435-20570) —
 * pastilles 24px, trait 1px, libellés 14px. Utilisé par toutes les pages de
 * création de la section Paramètres et présenté dans l'UI Kit.
 *
 * Props :
 * - steps        : [{ key?|id?, label }]
 * - currentIndex : index de l'étape active (0-based)
 * - maxReachedIndex (optionnel) : dernière étape atteignable (défaut = currentIndex)
 * - onStepClick (optionnel) : (index, step) => void. Si fourni, les étapes
 *   atteignables deviennent cliquables.
 */
const StepCheck = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    className="text-white"
  >
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Stepper = ({
  steps = [],
  currentIndex = 0,
  maxReachedIndex,
  onStepClick,
  className = "",
  ariaLabel = "Progression",
}) => {
  const clickable = typeof onStepClick === "function";
  const maxReached =
    typeof maxReachedIndex === "number" ? maxReachedIndex : currentIndex;

  return (
    <nav className={`ui-stepper ${className}`.trim()} aria-label={ariaLabel}>
      <ol className="ui-stepper__list">
        {steps.map((step, index) => {
          const key = step.key ?? step.id ?? index;
          const isDone = index < currentIndex;
          const isActive = index === currentIndex;
          const isReachable = index <= maxReached;
          const isLast = index === steps.length - 1;

          const bulletClass = [
            "ui-stepper__bullet",
            isDone || isActive
              ? "ui-stepper__bullet--current"
              : "ui-stepper__bullet--upcoming",
          ].join(" ");

          const labelClass = [
            "ui-stepper__label",
            isActive
              ? "ui-stepper__label--active"
              : isDone
                ? "ui-stepper__label--done"
                : "ui-stepper__label--upcoming",
          ].join(" ");

          const bulletContent = isDone ? (
            <StepCheck />
          ) : (
            <span className="ui-stepper__number">{index + 1}</span>
          );

          const lineClass = [
            "ui-stepper__line",
            isDone && "ui-stepper__line--done",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <li key={key} className="ui-stepper__item">
              {!isLast && <span className={lineClass} aria-hidden />}
              {clickable ? (
                <button
                  type="button"
                  disabled={!isReachable}
                  onClick={() => isReachable && onStepClick(index, step)}
                  className={bulletClass}
                  aria-current={isActive ? "step" : undefined}
                >
                  {bulletContent}
                </button>
              ) : (
                <span
                  className={bulletClass}
                  aria-current={isActive ? "step" : undefined}
                >
                  {bulletContent}
                </span>
              )}
              <span className={labelClass}>{step.label}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Stepper;
